import type { UserListQuery } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { RoleWithPermissions } from '../../roles/roles.api';
import { UserRolesCell } from './UserRolesCell';
import { userPath } from '../users.paths';
import type { UserListItem } from '../users.api';

interface BuildUserColumnsArgs {
  roleOptions: RoleWithPermissions[];
  /** The signed-in user, whose own row is marked "You" and can't be re-roled. */
  currentUserId: string | undefined;
  list: Pick<ListQueryControls<UserListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  onSetRoles: (user: UserListItem, roles: string[]) => Promise<void>;
}

const STATUS_FILTERS = [
  { text: 'Active', value: 'active' },
  { text: 'Inactive', value: 'inactive' },
];

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `userListQuerySchema`. */
export function buildUserColumns({ roleOptions, currentUserId, list, onSetRoles }: BuildUserColumnsArgs): TableColumnsType<UserListItem> {
  return [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: list.sortOrderFor('name'),
      render: (name: string, user) => (
        <span className="inline-flex items-center gap-2">
          <Link to={userPath(user.id)}>{name}</Link>
          {user.id === currentUserId && <Tag color="processing">You</Tag>}
        </span>
      ),
    },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: true, sortOrder: list.sortOrderFor('email') },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (phone: string | null) => phone ?? '—' },
    {
      title: 'Roles',
      key: 'role',
      filters: roleOptions.map((role) => ({ text: role.name, value: role.key })),
      filteredValue: list.filteredValueFor('role'),
      render: (_, user) => (
        <UserRolesCell roles={user.roles} roleOptions={roleOptions} canEdit={user.id !== currentUserId} onSave={(roles) => onSetRoles(user, roles)} />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      filters: STATUS_FILTERS,
      filteredValue: list.filteredValueFor('status'),
      render: (_, user) => <Tag color={user.isActive ? 'success' : 'default'}>{user.isActive ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Last login',
      key: 'lastLoginAt',
      sorter: true,
      sortOrder: list.sortOrderFor('lastLoginAt'),
      render: (_, user) => (user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'),
    },
  ];
}
